import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, ArrowLeft, Plus, MessageCircleHeart, Trash2, Edit, LogOut, Zap, Split, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChunkAnalysisDialog from '@/components/ChunkAnalysisDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function ChunkBank({ onLogout }) {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [chunks, setChunks] = useState([]);
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [quickEntryText, setQuickEntryText] = useState('');
  const [quickEntryParsed, setQuickEntryParsed] = useState([]);
  const [savingQuickEntry, setSavingQuickEntry] = useState(false);
  const [editingChunk, setEditingChunk] = useState(null);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [formData, setFormData] = useState({
    chunk_text: '',
    function: '',
    context: '',
    chunk_type: 'spontaneous',
    communication_style: 'GLP',
    glp_stage_observed: null,
    word_type: null,
    notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [childRes, chunksRes] = await Promise.all([
        axios.get(`${API}/children/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/chunks/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setChild(childRes.data);
      setChunks(chunksRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = { 
        ...formData, 
        child_id: childId,
        glp_stage_observed: formData.glp_stage_observed ? parseInt(formData.glp_stage_observed) : null
      };
      await axios.post(
        `${API}/chunks`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Language sample added!');
      setShowDialog(false);
      fetchData();
      setFormData({
        chunk_text: '',
        function: '',
        context: '',
        chunk_type: 'spontaneous',
        communication_style: child?.communication_style || 'GLP',
        glp_stage_observed: null,
        word_type: null,
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sample');
    }
  };

  const openEditDialog = (chunk) => {
    setEditingChunk(chunk);
    setShowEditDialog(true);
  };

  // Quick entry functions
  const parseQuickEntryText = (text) => {
    // Split by newlines or semicolons, filter empty lines
    const lines = text.split(/[\n;]+/).map(line => line.trim()).filter(line => line.length > 0);
    return lines.map(line => ({
      chunk_text: line,
      function: 'To be classified',
      context: 'Quick entry - add details later',
      chunk_type: 'spontaneous',
      communication_style: child?.communication_style || 'GLP',
      glp_stage_observed: null,
      word_type: null,
      notes: ''
    }));
  };

  const handleQuickEntryTextChange = (text) => {
    setQuickEntryText(text);
    setQuickEntryParsed(parseQuickEntryText(text));
  };

  const handleSaveQuickEntry = async () => {
    if (quickEntryParsed.length === 0) {
      toast.error('No gestalts to save');
      return;
    }
    
    setSavingQuickEntry(true);
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      
      for (const chunk of quickEntryParsed) {
        try {
          await axios.post(
            `${API}/chunks`,
            { ...chunk, child_id: childId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          successCount++;
        } catch (err) {
          console.error('Failed to save chunk:', chunk.chunk_text);
        }
      }
      
      toast.success(`${successCount} language sample${successCount !== 1 ? 's' : ''} added!`);
      setShowQuickEntry(false);
      setQuickEntryText('');
      setQuickEntryParsed([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to save samples');
    } finally {
      setSavingQuickEntry(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        chunk_text: editingChunk.chunk_text,
        function: editingChunk.function,
        context: editingChunk.context,
        chunk_type: editingChunk.chunk_type,
        communication_style: editingChunk.communication_style,
        glp_stage_observed: editingChunk.glp_stage_observed ? parseInt(editingChunk.glp_stage_observed) : null,
        word_type: editingChunk.word_type,
        notes: editingChunk.notes
      };
      await axios.put(`${API}/chunks/${editingChunk.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Language sample updated!');
      setShowEditDialog(false);
      setEditingChunk(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update sample');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this language sample?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chunks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sample deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sample');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(`/children/${childId}`)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
          <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                Language Samples
              </h2>
              <p className="text-lg text-stone-600">{child?.name}&apos;s words, phrases &amp; expressions</p>
            </div>
            {user.role === 'therapist' && (
              <div className="flex gap-2">
                {/* Quick Entry Button */}
                <Dialog open={showQuickEntry} onOpenChange={setShowQuickEntry}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="quick-entry-btn" className="rounded-full">
                      <Zap className="w-4 h-4 mr-2" />
                      Quick Entry
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quick-entry-dialog">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-serif flex items-center gap-2">
                        <Zap className="w-6 h-6 text-amber-500" />
                        Quick Gestalt Entry
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <strong>Tip:</strong> Type or paste multiple gestalts quickly. Put each one on a new line or separate with semicolons (;). You can add details to each sample later.
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Gestalts (one per line or separated by ;)</Label>
                        <Textarea
                          data-testid="quick-entry-textarea"
                          placeholder={`"Let's go!"\n"I want that one"\n"No more please"\n"Time to go home"`}
                          value={quickEntryText}
                          onChange={(e) => handleQuickEntryTextChange(e.target.value)}
                          className="min-h-40 rounded-2xl font-mono text-sm"
                        />
                      </div>
                      
                      {quickEntryParsed.length > 0 && (
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Split className="w-4 h-4" />
                            Preview ({quickEntryParsed.length} sample{quickEntryParsed.length !== 1 ? 's' : ''})
                          </Label>
                          <div className="max-h-40 overflow-y-auto space-y-2 p-3 bg-stone-50 rounded-xl">
                            {quickEntryParsed.map((chunk, index) => (
                              <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-stone-200">
                                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                                  {index + 1}
                                </span>
                                <span className="text-sm text-stone-800">&quot;{chunk.chunk_text}&quot;</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            setShowQuickEntry(false);
                            setQuickEntryText('');
                            setQuickEntryParsed([]);
                          }} 
                          className="flex-1 rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleSaveQuickEntry} 
                          disabled={savingQuickEntry || quickEntryParsed.length === 0}
                          className="flex-1 rounded-full"
                          data-testid="save-quick-entry-btn"
                        >
                          {savingQuickEntry ? 'Saving...' : `Save ${quickEntryParsed.length} Sample${quickEntryParsed.length !== 1 ? 's' : ''}`}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Analyze chunk bank button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAnalysisDialog(true)}
                  className="rounded-full"
                  data-testid="analyze-chunks-btn"
                  disabled={chunks.length === 0}
                  title={chunks.length === 0 ? 'Add a few samples first' : 'Analyze chunks'}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analyze
                </Button>

                {/* Regular Add Button */}
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-chunk-btn" className="rounded-full shadow-lg shadow-primary/20">
                    <Plus className="w-4 h-4 mr-2" />
                    Add sample
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="chunk-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif">Add language sample</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="chunk-text">Word or phrase</Label>
                      <Input
                        id="chunk-text"
                        data-testid="chunk-text-input"
                        placeholder="Let&apos;s go! / more / big truck"
                        value={formData.chunk_text}
                        onChange={(e) => setFormData({ ...formData, chunk_text: e.target.value })}
                        required
                        className="h-12 rounded-2xl"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Language type</Label>
                        <Select
                          value={formData.communication_style}
                          onValueChange={(value) => setFormData({ ...formData, communication_style: value })}
                        >
                          <SelectTrigger className="h-12 rounded-2xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GLP">GLP (Gestalt)</SelectItem>
                            <SelectItem value="Analytic">Analytic (Single words)</SelectItem>
                            <SelectItem value="Mixed">Mixed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {formData.communication_style === 'GLP' && (
                        <div className="space-y-2">
                          <Label>GLP Stage Observed</Label>
                          <Select
                            value={formData.glp_stage_observed?.toString() || 'none'}
                            onValueChange={(value) => setFormData({ ...formData, glp_stage_observed: value === 'none' ? null : value })}
                          >
                            <SelectTrigger className="h-12 rounded-2xl">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not specified</SelectItem>
                              <SelectItem value="1">Stage 1</SelectItem>
                              <SelectItem value="2">Stage 2</SelectItem>
                              <SelectItem value="3">Stage 3</SelectItem>
                              <SelectItem value="4">Stage 4</SelectItem>
                              <SelectItem value="5">Stage 5</SelectItem>
                              <SelectItem value="6">Stage 6</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {formData.communication_style === 'Analytic' && (
                        <div className="space-y-2">
                          <Label>Word type</Label>
                          <Select
                            value={formData.word_type || 'none'}
                            onValueChange={(value) => setFormData({ ...formData, word_type: value === 'none' ? null : value })}
                          >
                            <SelectTrigger className="h-12 rounded-2xl">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not specified</SelectItem>
                              <SelectItem value="noun">Noun</SelectItem>
                              <SelectItem value="verb">Verb</SelectItem>
                              <SelectItem value="adjective">Adjective</SelectItem>
                              <SelectItem value="phrase">Phrase</SelectItem>
                              <SelectItem value="sentence">Sentence</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="function">Function</Label>
                      <Input
                        id="function"
                        data-testid="function-input"
                        placeholder="Requesting, commenting, protesting..."
                        value={formData.function}
                        onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                        required
                        className="h-12 rounded-2xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="context">Context</Label>
                      <Textarea
                        id="context"
                        data-testid="context-input"
                        placeholder="When/where was this observed?"
                        value={formData.context}
                        onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                        required
                        className="rounded-2xl min-h-20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="chunk-type">Observation type</Label>
                      <Select
                        value={formData.chunk_type}
                        onValueChange={(value) => setFormData({ ...formData, chunk_type: value })}
                      >
                        <SelectTrigger data-testid="chunk-type-select" className="h-12 rounded-2xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="spontaneous">Spontaneous</SelectItem>
                          <SelectItem value="echoed">Echoed/Imitated</SelectItem>
                          <SelectItem value="novel_combination">Novel combination</SelectItem>
                          <SelectItem value="prompted">Prompted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        data-testid="notes-input"
                        placeholder="Additional observations..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="rounded-2xl min-h-20"
                      />
                    </div>
                    <Button type="submit" data-testid="create-chunk-btn" className="w-full h-12 rounded-full">
                      Add sample
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            )}
          </div>

          {user.role === 'parent' && (
            <div className="mb-8 p-6 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl">
              <p className="font-accent text-xl text-center text-primary-800">
                These are language patterns your child uses naturally. Try modeling them in play!
              </p>
            </div>
          )}

          {chunks.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <MessageCircleHeart className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">No chunks yet</h3>
              <p className="text-stone-500 mb-6">
                {user.role === 'therapist'
                  ? 'Start recording language patterns'
                  : 'Your therapist will add language chunks here'}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {chunks.map((chunk, index) => (
                <motion.div
                  key={chunk.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Card className="p-6 bg-white rounded-3xl border border-stone-100 hover:shadow-md transition-all relative" data-testid={`chunk-card-${chunk.id}`}>
                    {user.role === 'therapist' && (
                      <div className="absolute top-4 right-4 flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-chunk-btn-${chunk.id}`}
                          onClick={() => openEditDialog(chunk)}
                          className="rounded-full h-8 w-8 p-0 text-stone-500 hover:text-primary"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-chunk-btn-${chunk.id}`}
                          onClick={() => handleDelete(chunk.id)}
                          className="rounded-full h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      <div className="bg-secondary/10 rounded-2xl p-4">
                        <MessageCircleHeart className="w-6 h-6 text-secondary mb-2" />
                        <p className="text-2xl font-serif text-primary-900">&quot;{chunk.chunk_text}&quot;</p>
                      </div>

                      <div>
                        <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-1">Function</p>
                        <p className="text-base text-stone-700">{chunk.function}</p>
                      </div>

                      <div>
                        <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-1">Context</p>
                        <p className="text-sm text-stone-600 leading-relaxed">{chunk.context}</p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          chunk.chunk_type === 'spontaneous'
                            ? 'bg-green-100 text-green-700'
                            : chunk.chunk_type === 'echoed'
                            ? 'bg-blue-100 text-blue-700'
                            : chunk.chunk_type === 'novel_combination'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {chunk.chunk_type?.replace('_', ' ')}
                        </span>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          chunk.communication_style === 'GLP'
                            ? 'bg-primary/10 text-primary'
                            : chunk.communication_style === 'Analytic'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-stone-100 text-stone-600'
                        }`}>
                          {chunk.communication_style || 'GLP'}
                          {chunk.communication_style === 'GLP' && chunk.glp_stage_observed && ` Stage ${chunk.glp_stage_observed}`}
                          {chunk.communication_style === 'Analytic' && chunk.word_type && ` • ${chunk.word_type}`}
                        </span>
                      </div>

                      {chunk.notes && (
                        <div>
                          <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-1">Notes</p>
                          <p className="text-sm text-stone-600 italic">{chunk.notes}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
      
      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-chunk-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Edit language sample</DialogTitle>
          </DialogHeader>
          {editingChunk && (
            <form onSubmit={handleUpdate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Word or phrase</Label>
                <Input
                  value={editingChunk.chunk_text}
                  onChange={(e) => setEditingChunk({ ...editingChunk, chunk_text: e.target.value })}
                  required
                  className="h-12 rounded-2xl"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Language type</Label>
                  <Select
                    value={editingChunk.communication_style || 'GLP'}
                    onValueChange={(value) => setEditingChunk({ ...editingChunk, communication_style: value })}
                  >
                    <SelectTrigger className="h-12 rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GLP">GLP (Gestalt)</SelectItem>
                      <SelectItem value="Analytic">Analytic (Single words)</SelectItem>
                      <SelectItem value="Mixed">Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {editingChunk.communication_style === 'GLP' && (
                  <div className="space-y-2">
                    <Label>GLP Stage Observed</Label>
                    <Select
                      value={editingChunk.glp_stage_observed?.toString() || 'none'}
                      onValueChange={(value) => setEditingChunk({ ...editingChunk, glp_stage_observed: value === 'none' ? null : parseInt(value) })}
                    >
                      <SelectTrigger className="h-12 rounded-2xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        <SelectItem value="1">Stage 1</SelectItem>
                        <SelectItem value="2">Stage 2</SelectItem>
                        <SelectItem value="3">Stage 3</SelectItem>
                        <SelectItem value="4">Stage 4</SelectItem>
                        <SelectItem value="5">Stage 5</SelectItem>
                        <SelectItem value="6">Stage 6</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {editingChunk.communication_style === 'Analytic' && (
                  <div className="space-y-2">
                    <Label>Word type</Label>
                    <Select
                      value={editingChunk.word_type || 'none'}
                      onValueChange={(value) => setEditingChunk({ ...editingChunk, word_type: value === 'none' ? null : value })}
                    >
                      <SelectTrigger className="h-12 rounded-2xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        <SelectItem value="noun">Noun</SelectItem>
                        <SelectItem value="verb">Verb</SelectItem>
                        <SelectItem value="adjective">Adjective</SelectItem>
                        <SelectItem value="phrase">Phrase</SelectItem>
                        <SelectItem value="sentence">Sentence</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Function</Label>
                <Input
                  value={editingChunk.function}
                  onChange={(e) => setEditingChunk({ ...editingChunk, function: e.target.value })}
                  required
                  className="h-12 rounded-2xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Context</Label>
                <Textarea
                  value={editingChunk.context}
                  onChange={(e) => setEditingChunk({ ...editingChunk, context: e.target.value })}
                  required
                  className="rounded-2xl min-h-20"
                />
              </div>
              <div className="space-y-2">
                <Label>Observation type</Label>
                <Select
                  value={editingChunk.chunk_type}
                  onValueChange={(value) => setEditingChunk({ ...editingChunk, chunk_type: value })}
                >
                  <SelectTrigger className="h-12 rounded-2xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spontaneous">Spontaneous</SelectItem>
                    <SelectItem value="echoed">Echoed/Imitated</SelectItem>
                    <SelectItem value="novel_combination">Novel combination</SelectItem>
                    <SelectItem value="prompted">Prompted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingChunk.notes || ''}
                  onChange={(e) => setEditingChunk({ ...editingChunk, notes: e.target.value })}
                  className="rounded-2xl min-h-20"
                />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full">
                  Save changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ChunkAnalysisDialog
        open={showAnalysisDialog}
        onOpenChange={setShowAnalysisDialog}
        childId={childId}
      />
    </div>
  );
}
